import * as React from 'react';
import cx from 'classnames';
import { map, capitalize } from 'lodash';
import { Tooltip } from 'antd';
import {
  AlignLeftIcon,
  AlignCenterIcon,
  AlignRightIcon,
  AlignJustifyIcon,
} from '@revfluence/fresh-icons/regular/esm';

import { IndentDecreaseIcon, IndentIncreaseIcon } from 'lucide-react';
import { TTextAlign, TIndentType } from './types';

const ALIGNMENT_ICON: {
  [key in TTextAlign]: React.ReactNode;
} = {
  left: <AlignLeftIcon width={14} height={14} />,
  center: <AlignCenterIcon width={14} height={14} />,
  right: <AlignRightIcon width={14} height={14} />,
  justify: <AlignJustifyIcon width={14} height={14} />,
};

const ALIGNMENT_OPTIONS: TTextAlign[] = ['left', 'center', 'right', 'justify'];

const ALIGNMENT_HOTKEY: {
  [key in TTextAlign]: string;
} = {
  left: '⌘⇧L',
  center: '⌘⇧E',
  right: '⌘⇧R',
  justify: '⌘⇧J',
};

const INDENT_ICON: {
  [key in TIndentType]: React.ReactNode;
} = {
  indent: <IndentIncreaseIcon width={14} height={14} />,
  outdent: <IndentDecreaseIcon width={14} height={14} />,
};

const INDENT_OPTIONS: TIndentType[] = ['outdent', 'indent'];

const INDENT_HOTKEY: {
  [key in TIndentType]: string;
} = {
  indent: '⌘]',
  outdent: '⌘[',
};

const INDENT_LABEL: {
  [key in TIndentType]: string;
} = {
  indent: 'Increase Indent',
  outdent: 'Decrease Indent',
};

interface IProps {
  currentAlignment?: TTextAlign;
  onAlignmentChange(alignment: TTextAlign): void;
  onIndentChange(type: TIndentType): void;
  className?: string;
}

export const AlignmentControls: React.FC<IProps> = React.memo((props) => {
  const { currentAlignment = 'left' } = props;

  return (
    <div className={cx('flex items-center gap-2', props.className)}>
      <div className="flex items-center gap-0.5">
        {map(ALIGNMENT_OPTIONS, (alignment) => (
          <Tooltip
            key={alignment}
            title={
              <div className="flex items-center gap-2">
                {capitalize(alignment)}
                <span className="text-xs bg-white bg-opacity-20 px-1 py-0.5 rounded">
                  {ALIGNMENT_HOTKEY[alignment]}
                </span>
              </div>
            }
          >
            <div
              className={cx(
                'p-1.5 rounded cursor-pointer flex items-center justify-center transition-all duration-200 hover:bg-gray-100',
                {
                  'bg-blue-50 text-blue-600': currentAlignment === alignment,
                }
              )}
              onMouseDown={(event) => {
                event.preventDefault();
                props.onAlignmentChange(alignment);
              }}
            >
              {ALIGNMENT_ICON[alignment]}
            </div>
          </Tooltip>
        ))}
      </div>

      <div className="w-px h-5 bg-gray-300 mx-1" />

      <div className="flex items-center gap-0.5">
        {map(INDENT_OPTIONS, (indentType) => (
          <Tooltip
            key={indentType}
            title={
              <div className="flex items-center gap-2">
                {INDENT_LABEL[indentType]}
                <span className="text-xs bg-white bg-opacity-20 px-1 py-0.5 rounded">
                  {INDENT_HOTKEY[indentType]}
                </span>
              </div>
            }
          >
            <div
              className="p-1.5 rounded cursor-pointer flex items-center justify-center transition-all duration-200 hover:bg-gray-100"
              onMouseDown={(event) => {
                event.preventDefault();
                props.onIndentChange(indentType);
              }}
            >
              {INDENT_ICON[indentType]}
            </div>
          </Tooltip>
        ))}
      </div>
    </div>
  );
});

AlignmentControls.displayName = 'AlignmentControls';
